import * as z from 'zod/mini';
import {
  cableTypeSchema,
  dropColorSchema,
  lengthUnitSchema,
  testedOnSchema,
  testResultSchema,
} from '../cable.ts';
import { idSchema, notesSchema, optionalNameSchema, timestampSchema } from './common.ts';
import type { FiberMode } from '../cable.ts';

// A link between two devices of one client. Each end names an item and,
// for devices with numbered ports (switches, patch panels), the port. The
// medium says how the link is made: copper cable or fiber (either may run
// through one of the room's cable drops between rooms), a wireless mesh
// hop, or a VPN between gateways at two locations. Cable and fiber links
// record their termination; fiber links their mode.

export const CONNECTION_MEDIA = [
  'cable',
  'fiber',
  'mesh',
  'vpn',
  // Signal beyond the network (ROADMAP J6): the picture, sound, and control
  // wiring an AV or automation install is made of. They are physical links
  // like a cable, but none of the network media's rules fits them — see
  // AV_MEDIA below and the rule table in shared/connections.ts.
  'hdmi',
  'sdi',
  'audio',
  'speaker',
  'rs232',
  'ir',
  'relay',
] as const;
export type ConnectionMedium = (typeof CONNECTION_MEDIA)[number];
export const connectionMediumSchema = z.enum(CONNECTION_MEDIA);

export const CONNECTION_MEDIUM_LABELS: Readonly<Record<ConnectionMedium, string>> = {
  cable: 'Ethernet cable',
  fiber: 'Fiber',
  mesh: 'Mesh (wireless)',
  vpn: 'VPN',
  hdmi: 'HDMI',
  sdi: 'SDI',
  audio: 'Analogue audio',
  speaker: 'Speaker wire',
  rs232: 'RS-232',
  ir: 'IR',
  relay: 'Relay / contact closure',
};

/**
 * The AV and control media (ROADMAP J6). Unlike ethernet and fiber they land
 * on no numbered port (an amplifier's outputs are not the ports this product
 * counts), carry no termination or fiber mode, and consume no cable drop: a
 * speaker run or an IR line is its own cable, recorded on the link itself.
 * What they do share with a wired link is that both devices must stand at one
 * property, since the wire has to reach.
 */
export const AV_MEDIA = [
  'hdmi',
  'sdi',
  'audio',
  'speaker',
  'rs232',
  'ir',
  'relay',
] as const satisfies readonly ConnectionMedium[];
export type AvMedium = (typeof AV_MEDIA)[number];

const AV_MEDIA_SET: ReadonlySet<ConnectionMedium> = new Set<ConnectionMedium>(AV_MEDIA);

export function isAvMedium(medium: ConnectionMedium): medium is AvMedium {
  return AV_MEDIA_SET.has(medium);
}

export const FIBER_MODES = ['single_mode', 'multi_mode'] as const satisfies readonly FiberMode[];
export const fiberModeSchema = z.enum(FIBER_MODES);
export const FIBER_MODE_LABELS: Readonly<Record<FiberMode, string>> = {
  single_mode: 'Single-mode',
  multi_mode: 'Multi-mode',
};

/** How a link's cable ends: copper takes RJ45 or keystone, fiber LC or SC. */
export const LINK_TERMINATIONS = ['rj45', 'keystone', 'lc', 'sc'] as const;
export type LinkTermination = (typeof LINK_TERMINATIONS)[number];
export const linkTerminationSchema = z.enum(LINK_TERMINATIONS);
export const LINK_TERMINATION_LABELS: Readonly<Record<LinkTermination, string>> = {
  rj45: 'RJ45',
  keystone: 'Keystone',
  lc: 'LC',
  sc: 'SC',
};
export const COPPER_TERMINATIONS: readonly LinkTermination[] = ['rj45', 'keystone'];
export const FIBER_TERMINATIONS: readonly LinkTermination[] = ['lc', 'sc'];

/**
 * Where a link stands in its life: planned before it is run, connected once
 * it is, decommissioning while it is being pulled out. The trace and the map
 * count a planned link as not yet carrying traffic.
 */
export const LINK_STATUSES = ['planned', 'connected', 'decommissioning'] as const;
export type LinkStatus = (typeof LINK_STATUSES)[number];
export const linkStatusSchema = z.enum(LINK_STATUSES);
export const LINK_STATUS_LABELS: Readonly<Record<LinkStatus, string>> = {
  planned: 'Planned',
  connected: 'Connected',
  decommissioning: 'Decommissioning',
};
export const DEFAULT_LINK_STATUS: LinkStatus = 'connected';

export const connectionEndpointSchema = z.object({
  itemId: idSchema,
  port: z.nullable(z.int().check(z.positive())),
  /**
   * The position within a multi-position port (ROADMAP I2): the leg of an MPO
   * or breakout port a link lands on. Absent or null means the whole port — a
   * plain single-position port, as every link recorded before I2 — so an old
   * endpoint reads unchanged.
   */
  position: z.optional(z.nullable(z.int().check(z.positive()))),
});

// The deepening fields (ROADMAP I1/I4): what a direct patch cord is made of
// and where it stands. A link carried over a cable drop leaves these blank
// and reads them off the run instead (shared/connections.ts resolves the
// effective values), so the run stays the one place its own cable is
// recorded; a direct link (a patch cord in a rack, a jumper between panels)
// carries them itself.
const linkDetailFields = {
  /** A cable ID or circuit label printed on the run; '' when unlabelled. */
  label: optionalNameSchema,
  cableType: z.nullable(cableTypeSchema),
  color: z.nullable(dropColorSchema),
  lengthValue: z.nullable(z.number().check(z.nonnegative())),
  lengthUnit: lengthUnitSchema,
  linkStatus: linkStatusSchema,
  testedOn: testedOnSchema,
  testResult: testResultSchema,
};

export const connectionSchema = z.object({
  id: idSchema,
  clientId: idSchema,
  a: connectionEndpointSchema,
  b: connectionEndpointSchema,
  medium: connectionMediumSchema,
  fiberMode: z.nullable(fiberModeSchema),
  termination: z.nullable(linkTerminationSchema),
  dropId: z.nullable(idSchema),
  ...linkDetailFields,
  notes: z.string(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type Connection = z.infer<typeof connectionSchema>;

export const connectionInputSchema = z.strictObject({
  a: connectionEndpointSchema,
  b: connectionEndpointSchema,
  medium: connectionMediumSchema,
  fiberMode: z.nullable(fiberModeSchema),
  termination: z.nullable(linkTerminationSchema),
  dropId: z.nullable(idSchema),
  // An older client, and the run-link sync, send none of the deepening
  // fields; each falls back to its blank so the link is still made.
  label: z._default(linkDetailFields.label, ''),
  cableType: z._default(linkDetailFields.cableType, null),
  color: z._default(linkDetailFields.color, null),
  lengthValue: z._default(linkDetailFields.lengthValue, null),
  lengthUnit: z._default(linkDetailFields.lengthUnit, 'ft'),
  linkStatus: z._default(linkDetailFields.linkStatus, DEFAULT_LINK_STATUS),
  testedOn: z._default(linkDetailFields.testedOn, null),
  testResult: z._default(linkDetailFields.testResult, ''),
  notes: notesSchema,
});
export type ConnectionInput = z.infer<typeof connectionInputSchema>;

/**
 * The deepening fields at their blanks: no label, no cable of its own, the
 * default status, untested. The run-link sync and any caller building a link
 * that reads its cable off a run (rather than carrying its own) spreads these
 * so it need not restate every default.
 */
export const BLANK_LINK_DETAILS = {
  label: '',
  cableType: null,
  color: null,
  lengthValue: null,
  lengthUnit: 'ft',
  linkStatus: DEFAULT_LINK_STATUS,
  testedOn: null,
  testResult: '',
} as const satisfies Pick<
  ConnectionInput,
  | 'label'
  | 'cableType'
  | 'color'
  | 'lengthValue'
  | 'lengthUnit'
  | 'linkStatus'
  | 'testedOn'
  | 'testResult'
>;

export const connectionListSchema = z.object({ connections: z.array(connectionSchema) });
export const connectionEnvelopeSchema = z.object({ connection: connectionSchema });
